import { Shop } from '../../../types';
import { SalesResponse } from '../../sales/sales.type';
import {
  decodePaperStatus,
  escposBuilder,
  PaperStatus,
  statusRequest
} from './escpos';
import {
  buildCardReceipt,
  buildSaleReceipt,
  buildTestPage
} from './receipt-layout';
import { webSerial } from './web-serial.service';

/**
 * The printer attached to this till, driven over Web Serial with no
 * connector in between.
 *
 * Every job goes through one queue: two receipts interleaved on one serial
 * port would come out as a single unreadable slip.
 *
 * Ported from POSUI client/js/shared/serial/serialdevice.service.js, minus the
 * connector route switch - Simple POS calls these functions directly.
 */

export const BAUD_RATES = [9600, 19200, 38400, 57600, 115200];

let queue: Promise<unknown> = Promise.resolve();

const enqueue = <T>(job: () => Promise<T>): Promise<T> => {
  const work = queue.then(job);
  // Keep the chain alive for the next caller even when this job failed.
  queue = work.catch(() => undefined);
  return work;
};

/**
 * A port granted earlier can be reopened without a user gesture, which covers
 * the normal case of the browser having been restarted.
 */
const ensureConnected = async () => {
  if (webSerial.isConnected()) return;
  const connected = await webSerial.autoConnect();
  if (!connected) {
    throw new Error(
      'No printer connected. Open Supervisor Functions and choose Connect printer.'
    );
  }
};

const print = (build: () => Uint8Array) =>
  enqueue(async () => {
    await ensureConnected();
    await webSerial.write(build());
  });

const queryStatus = (request: Uint8Array): Promise<number | null> =>
  enqueue(async () => {
    await ensureConnected();
    await webSerial.write(request);
    const bytes = await webSerial.read(400, 1);
    return bytes.length ? bytes[0] : null;
  }).catch(() => null);

const hasCashPayment = (sale: SalesResponse) =>
  (sale.receipt.payments || []).some(p => p.paymentType === 'Cash');

export const localPrinter = {
  /** True once a printer is open, reopening a granted port if need be. */
  ensureReady: async () => {
    try {
      await ensureConnected();
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Print a sale. The drawer is kicked in the same write when cash was taken,
   * so it opens as the receipt starts rather than after a second round trip.
   */
  printSale: (
    sale: SalesResponse,
    options: { shop?: Shop; isCopy?: boolean; openDrawer?: boolean } = {}
  ) =>
    print(() => {
      if (!sale || !sale.receipt) {
        throw new Error('The sale came back without a receipt to print.');
      }
      // eslint-disable-next-line no-console
      console.info('Local device - printing receipt', sale.receipt.receiptId);
      const receipt = buildSaleReceipt(sale, options);
      const kick =
        options.openDrawer === undefined
          ? !options.isCopy && hasCashPayment(sale)
          : options.openDrawer;
      if (!kick) return receipt;
      const drawer = escposBuilder()
        .openDrawer()
        .bytes();
      const bytes = new Uint8Array(drawer.length + receipt.length);
      bytes.set(drawer, 0);
      bytes.set(receipt, drawer.length);
      return bytes;
    }),

  printCardReceipt: (text: string) => print(() => buildCardReceipt(text)),

  printTestPage: (shop?: Shop) => print(() => buildTestPage(shop)),

  openDrawer: () =>
    print(() =>
      escposBuilder()
        .openDrawer()
        .bytes()
    ),

  paperStatus: async (): Promise<PaperStatus> =>
    decodePaperStatus(await queryStatus(statusRequest.paper)),

  /**
   * A wrong line speed prints noise, which looks exactly like a broken cable.
   * This prints a labelled marker at each candidate rate so the paper answers
   * the question; the user then picks the one they can read.
   */
  findBaudRate: (rates: number[] = BAUD_RATES) =>
    enqueue(async () => {
      await ensureConnected();
      const restore = webSerial.getBaudRate();

      // Sequential on purpose: each rate needs the port to itself.
      // eslint-disable-next-line no-restricted-syntax
      for (const rate of rates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await webSerial.reopenAt(rate);
          // eslint-disable-next-line no-await-in-loop
          await webSerial.write(
            escposBuilder()
              .init()
              .left()
              .line(`---- ${rate} baud ----`)
              .feed(1)
              .bytes()
          );
        } catch (err) {
          // A rate the adapter will not accept is a normal outcome.
          // eslint-disable-next-line
          console.warn(`Local printer: ${rate} baud rejected`, err);
        }
      }

      // Leave the paper where it can be torn off, then go back to the saved rate.
      try {
        await webSerial.reopenAt(restore);
        await webSerial.write(
          escposBuilder()
            .feed(4)
            .cut()
            .bytes()
        );
      } catch (e) {
        // The saved rate failed to reopen; the status panel will show it.
      }
      return rates;
    }),

  useBaudRate: (rate: number) => enqueue(() => webSerial.useBaudRate(rate))
};
